<template>
  <dn-skeleton
    v-if="skeleton || loading"
    class="dn-most-read-card"
    :show-media="false"
    :lines="2"
  />
  <dn-link
    v-else
    :href="teaser.canonicalSlug"
    :class="['dn-most-read-card', theme?.toLowerCase()]"
  >
    <dn-card
      v-track-event="trackBinding"
      :title="teaser.title"
      :meta="teaser.meta"
      :data-attr="data_attr"
    />
  </dn-link>
</template>

<script setup lang="ts">
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: false,
    default: null
  },
  meta: {
    type: String,
    required: false,
    default: null
  },
  theme: {
    type: String,
    required: false,
    default: null
  },
  prefetch: {
    type: Boolean,
    required: false,
    default: true
  },
  /** Renders the card as a loading skeleton without fetching (used by the collection while its list loads). */
  skeleton: {
    type: Boolean,
    required: false,
    default: false
  },
  data_attr: {
    type: Number,
    required: false,
    default: null
  },
  /** See DnTeaserCard.vue for the position/total rationale. */
  position: {
    type: Number,
    required: false,
    default: 0
  },
  total: {
    type: Number,
    required: false,
    default: 0
  }
});

const trackBinding = () => ({
  eventName: 'article clicked',
  component: { zone: 'most-read', element: 'dn-card' },
  attributes: {
    ...(props.position && props.total ? { element_position: `${props.position}/${props.total}` } : {}),
    title: teaser.value.title,
    canonical_slug: teaser.value.canonicalSlug,
    meta: teaser.value.meta,
    zone_type: zoneType
  },
  // contract-only — sourced from data/props, not added to the teaser view-model
  content: {
    id: props.id || data.value?.id || '',
    type: data.value?.type || '',
    paid: data.value?.accessLevel === 'paywall',
    publication: data.value?.publication?.name || '',
    category: data.value?.categories?.find(category => category.default)?.title || data.value?.categories?.[0]?.title || '',
    topic: data.value?.topic?.name || ''
  }
});

const provider = inject('teaser_provider');
const zoneType = inject('zone_type', ZONE_TYPE_MANUAL);

const { query } = useTansQuery();

/*
 * Reactive so a reused card instance (skeleton placeholder → real item) fetches
 * once `skeleton` turns off and a real `id` arrives, without needing a `:key` remount.
 */
const shouldFetch = computed(() => !!(props.prefetch && provider && props.id && !props.skeleton));

const { data, loading } = (() => {
  if (!provider) {
    return { data: ref({}), loading: ref(false) };
  }
  try {
    const providerConfig = useRuntimeConfig()?.public?.[provider];
    const api = import.meta.client && providerConfig?.api ? providerConfig?.api : useRuntimeConfig()?.[provider]?.originApi;
    if (!api) {
      return { data: ref({}), loading: ref(false) };
    }
    const { replaceUrl } = useUrlReplace();
    const apiUrl = computed(() => replaceUrl(api, { id: props.id }));
    const authHeader = kongApiAuthHeader(useRuntimeConfig().dnmgAuthToken as string);
    return query({
      api: apiUrl,
      options: computed(() => ({ provider, id: props.id })),
      transform: transformTeaser,
      headers: authHeader,
      enabled: shouldFetch
    });
  } catch (e) {
    throw createError(`Error fetching teaser ${props.id} data for ${provider || 'undefined provider'}`);
  }
})();

const teaser = computed(() => {
  const title = props.title || data.value?.title || ' ';
  return {
    title,
    canonicalSlug: data.value?.canonicalSlug || `/${props.id}`,
    data_attr: props.data_attr,
    meta: props.meta || data.value?.categories?.find(category => category.default)?.title
  };
});
</script>

<style lang="scss">
.dn-most-read-card {
  --dn-link_display: grid;
  --dn-link_color: var(--dn-most-read--link-color);
  --dn-card_border-bottom-width: var(--dn-most-read--card_border-bottom-width);
  --dn-card_border-bottom-color: var(--dn-most-read--card_border-bottom-color);
  --dn-card_border-right-width: var(--dn-most-read--card_border-right-width);
  --dn-card_border-right-color: var(--dn-most-read--card_border-right-color);
  --dn-card_padding: var(--dn-most-read--card_padding);
  --dn-card_font-family: var(--dn-most-read--card_font-family);
  --dn-card_grid-template: var(--dn-most-read--card_grid-template);
  --dn-card_title_grid-area: var(--dn-most-read--card_title_grid-area);
  --dn-card_meta_grid-area: var(--dn-most-read--card_meta_grid-area);
  --dn-card--title_spacing: var(--dn-most-read--title_spacing);
  --dn-card--meta_font-family: var(--dn-most-read--meta_font-family);
  --dn-card--meta_color: var(--dn-most-read--meta_color);
  --dn-card--meta_spacing: var(--dn-most-read--meta_spacing);

  container-type: inline-size;
}
</style>
